// shell/components/entity-fields/view-renderers/date-renderer.ts

import { createElement } from '../../../utils/dom';
import { formatDate, formatDateTime, formatRelative } from '../../../utils/format';
import type { DateViewField } from '../../entity-view/types';

/**
 * Renders a date field value with format options.
 */
export function renderDateView<T>(
    field: DateViewField<T>,
    value: unknown,
    _model: T
): HTMLElement {
    const container = createElement('div', {
        className: 'ev-field-view ev-field-view--date',
    });

    if (value === null || value === undefined) {
        container.classList.add('ev-field-view--empty');
        container.textContent = '—';
        return container;
    }

    const date = parseDate(value);
    if (!date) {
        container.classList.add('ev-field-view--empty');
        container.textContent = '—';
        return container;
    }

    const format = field.format ?? 'date';
    let displayValue: string;

    switch (format) {
        case 'datetime':
            displayValue = formatDateTime(date);
            break;
        case 'relative':
            displayValue = formatRelative(date);
            // Add title with full date for accessibility
            container.title = formatDateTime(date);
            break;
        case 'date':
        default:
            displayValue = formatDate(date);
            break;
    }

    container.textContent = displayValue;
    return container;
}

/**
 * Parse a value into a Date object.
 */
function parseDate(value: unknown): Date | null {
    if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === 'string' || typeof value === 'number') {
        const date = new Date(value);
        return isNaN(date.getTime()) ? null : date;
    }
    return null;
}

